import { InputType } from 'config'
import { fullCountries } from 'config/states.constants'

const countryNameToCode: Record<string, string> = Object.fromEntries(
  Object.entries(fullCountries).map(([code, name]) => [name, code]),
) as Record<string, string>

/** ISO 2-letter code from a value selected in the Country dropdown (full country name). */
export const getCountryCodeFromCountryName = (countryName: string | undefined | null): string => {
  if (!countryName) return ''
  return countryNameToCode[countryName] || ''
}

export const PRIMARY_EMPLOYMENT_FIELD_KEYS: string[] = [
  'status',
  'employer',
  'phone',
  'fax',
  'employerEmail',
  'attn',
  'address',
  'country',
  'countryCode',
  'hasUnit',
  'unitNo',
  'positionTitle',
  'typeOfBusiness',
  'startDate',
  'lineOfWorkYears',
  'lineOfWorkMonths',
  'employedByFamilyOrParty',
  'selfEmployedOrBusinessOwner',
  'ownershipShare',
  'moIncomeLoss',
  'seasonalIncome',
  'foreignIncome',
  'notes',
]

const countryNameOptions = Object.values(fullCountries).sort((a, b) => a.localeCompare(b))

const ownershipShareOptions = ['Less Than 25 Percent', 'Greater Than Or Equal To 25 Percent']

export const getPrimaryEmploymentInputDefs = (): Record<string, InputType> => ({
  status: {
    inputType: 'text',
    title: 'Status',
    value: 'Primary',
    disabled: true,
    required: false,
  },
  employer: {
    inputType: 'text',
    title: 'Employer',
  },
  phone: {
    inputType: 'text',
    type: 'phone',
    title: 'Phone',
  },
  fax: {
    inputType: 'text',
    title: 'Fax',
  },
  employerEmail: {
    inputType: 'text',
    type: 'email',
    title: 'E-Mail',
  },
  attn: {
    inputType: 'text',
    title: 'Attention',
  },
  address: {
    inputType: 'map',
    title: 'Address',
    allowForeign: true,
    span: 'full',
  },
  country: {
    inputType: 'select',
    title: 'Country',
    options: countryNameOptions,
    hasDefaultOption: true,
  },
  countryCode: {
    inputType: 'text',
    title: 'Country Code',
    readOnly: true,
    required: false,
  },
  hasUnit: {
    inputType: 'select',
    title: 'Has Unit #',
    options: ['Yes', 'No'],
    hasDefaultOption: true,
  },
  unitNo: {
    inputType: 'text',
    type: 'thousandSepNoDecimal',
    title: 'Unit #',
    visible: (inputs: Record<string, InputType>) => inputs.hasUnit?.value === 'Yes',
  },
  positionTitle: {
    inputType: 'text',
    title: 'Position/Title',
  },
  typeOfBusiness: {
    inputType: 'text',
    title: 'Type of Bus.',
  },
  startDate: {
    inputType: 'text',
    type: 'date',
    title: 'Start Date',
  },
  lineOfWorkYears: {
    inputType: 'text',
    type: 'thousandSepNoDecimal',
    title: 'Time in Line of Work (Years)',
  },
  lineOfWorkMonths: {
    inputType: 'text',
    type: 'thousandSepNoDecimal',
    title: 'Time in Line of Work (Months)',
  },
  employedByFamilyOrParty: {
    inputType: 'select',
    title: 'Employed by Family or Party to Transaction',
    options: ['Yes', 'No'],
    hasDefaultOption: true,
  },
  selfEmployedOrBusinessOwner: {
    inputType: 'checkbox',
    title: 'Self Employed or Business Owner',
    value: false,
    required: false,
  },
  ownershipShare: {
    inputType: 'select',
    title: 'Ownership Share',
    options: ownershipShareOptions,
    hasDefaultOption: true,
    visible: (inputs: Record<string, InputType>) => inputs.selfEmployedOrBusinessOwner?.value === true,
  },
  moIncomeLoss: {
    inputType: 'text',
    type: 'thousandSep',
    title: 'Mo. Income / Loss',
    prefix: '$',
    visible: (inputs: Record<string, InputType>) => inputs.selfEmployedOrBusinessOwner?.value === true,
  },
  seasonalIncome: {
    inputType: 'checkbox',
    title: 'Seasonal Income',
    value: false,
    required: false,
  },
  foreignIncome: {
    inputType: 'checkbox',
    title: 'Foreign Income',
    value: false,
    required: false,
  },
  notes: {
    inputType: 'textarea',
    title: 'Notes',
    maxLength: 2000,
    rows: 5,
    span: 'full',
  },
})

export const getDefaultPrimaryEmploymentValues = (): Record<string, any> => ({
  status: 'Primary',
  employer: '',
  phone: '',
  fax: '',
  employerEmail: '',
  attn: '',
  address: '',
  country: '',
  countryCode: '',
  hasUnit: '',
  unitNo: '',
  positionTitle: '',
  typeOfBusiness: '',
  startDate: '',
  lineOfWorkYears: '',
  lineOfWorkMonths: '',
  employedByFamilyOrParty: '',
  selfEmployedOrBusinessOwner: false,
  ownershipShare: '',
  moIncomeLoss: '',
  seasonalIncome: false,
  foreignIncome: false,
  notes: '',
})

export const getDefaultSecondaryEmploymentValues = (): Record<string, any> => ({
  ...getDefaultPrimaryEmploymentValues(),
  status: 'Secondary',
  id: '',
  grossMonthlyIncome: [] as Record<string, any>[],
})

/** Field keys rendered in the Former Employment modal (subset of primary, plus endDate). */
export const FORMER_EMPLOYMENT_FIELD_KEYS: string[] = [
  'status',
  'employer',
  'phone',
  'fax',
  'employerEmail',
  'attn',
  'address',
  'country',
  'countryCode',
  'hasUnit',
  'unitNo',
  'positionTitle',
  'typeOfBusiness',
  'startDate',
  'endDate',
  'selfEmployedOrBusinessOwner',
  'notes',
]

export const getFormerEmploymentInputDefs = (): Record<string, InputType> => {
  const base = getPrimaryEmploymentInputDefs()
  return {
    ...base,
    endDate: {
      inputType: 'text',
      type: 'date',
      title: 'End Date',
    },
  }
}

export const getDefaultFormerEmploymentValues = (): Record<string, any> => ({
  status: 'Former',
  id: '',
  employer: '',
  phone: '',
  fax: '',
  employerEmail: '',
  attn: '',
  address: '',
  country: '',
  countryCode: '',
  hasUnit: '',
  unitNo: '',
  positionTitle: '',
  typeOfBusiness: '',
  startDate: '',
  endDate: '',
  selfEmployedOrBusinessOwner: false,
  notes: '',
  /** Single amount field (string with optional thousand formatting), not a table. */
  grossMonthlyIncome: '',
})
